import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { RootState } from '../../../../store';
import type { DataDefinition } from '../../../../store/dataDefinitionsSlice';
import { applyDefinition } from '../../utils/definitionRefs';
import { formatDateTime } from '../../../../utils/formatters';
import { Section, selectCls } from './ui';

/** 정의 경로에서 종류 폴더(sam/·db/)를 뺀 표시용 경로 */
const shownPath = (d: DataDefinition) => (d.path || '').split('/').slice(1).join('/');

/**
 * 데이터 노드(SAM파일·DB테이블)가 가리키는 **데이터 정의** — 목록에서 고르기 · 최종 갱신일시.
 *
 * 노드에는 정의 id(`config.dataDefinitionId`)만 저장되고, 경로·형식(또는 연결·스키마·테이블)·항목은 쓸 때
 * 정의에서 읽는다(utils/definitionRefs). 고르는 즉시 그 값이 화면에 채워지고, 그 칸들은 읽기 전용이 된다 —
 * 바꾸려면 정의를 고치거나 연결을 끊는다(끊으면 지금 값이 노드에 남아 직접 고칠 수 있다).
 * 고르지 않아도 된다(필수 아님): 경로·테이블을 직접 적은 노드도 그대로 돈다.
 */
const DefinitionLinkSection: React.FC<{
  node: any;
  defType: 'sam' | 'db';
  /** 노드 설정에 덮어쓸 값 — 패널의 patch */
  patch: (p: Record<string, any>) => void;
}> = ({ node, defType, patch }) => {
  const { t } = useTranslation();
  const all = useSelector((s: RootState) => s.dataDefinitions.items);
  const defs = useMemo(() => all.filter((d) => d.type === defType)
    .sort((a, b) => `${a.path}/${a.name}`.localeCompare(`${b.path}/${b.name}`)), [all, defType]);
  const cfg = node?.data?.config || {};
  const id: string = cfg.dataDefinitionId || '';
  const def = id ? defs.find((d) => d.id === id) : undefined;
  // 목록을 아직 못 받았으면(비어 있으면) '삭제됨' 이라고 단정하지 않는다 — 캔버스 노드와 같은 기준
  const gone = !!id && !def && all.length > 0;

  const choose = (nextId: string) => {
    // 연결을 끊으면 지금 값(정의에서 읽어 둔 것)이 노드에 남는다 — 이어서 직접 고칠 수 있게
    if (!nextId) { patch({ dataDefinitionId: undefined }); return; }
    const picked = defs.find((d) => d.id === nextId);
    if (picked) patch({ ...applyDefinition(cfg, picked), dataDefinitionId: picked.id });
  };

  return (
    <Section title={t('property:deflink.title')} right={def?.updatedAt ? (
      <span className="text-[0.6875rem] text-gray-500" title={t('property:deflink.updatedAt', { at: formatDateTime(def.updatedAt) })}>
        {t('property:deflink.updatedAt', { at: formatDateTime(def.updatedAt, { seconds: false }) })}
      </span>
    ) : undefined}>
      <select className={selectCls} aria-label={t('property:deflink.title')} value={id} onChange={(e) => choose(e.target.value)}>
        <option value="">{t('property:deflink.none')}</option>
        {gone && <option value={id}>{t('property:deflink.goneOption', { id })}</option>}
        {defs.map((d) => <option key={d.id} value={d.id}>{shownPath(d) ? `${shownPath(d)} / ${d.name}` : d.name}</option>)}
      </select>
      <div className={`mt-1 text-[0.6875rem] ${gone ? 'text-red-600' : 'text-gray-500'}`}>
        {gone ? t('property:deflink.gone') : def ? t('property:deflink.linked') : t('property:deflink.hint')}
      </div>
    </Section>
  );
};

export default DefinitionLinkSection;
